'use client'

import { createContext, useCallback, useContext, useState } from 'react'
import Confetti from 'react-confetti'
import { useWindowSize } from 'react-use'

const ConfettiContext = createContext({
  show: (time: number) => {},
})

/**
 * Provides a context for displaying confetti animations within the application.
 *
 * The `ConfettiProvider` manages the state and logic for showing a confetti effect,
 * exposing a `show` method via context to trigger the animation. The confetti effect
 * is rendered as an overlay when active, and automatically fades out after a specified duration.
 *
 * @param children - The React node(s) that will have access to the confetti context.
 *
 * @example
 * ```tsx
 * <ConfettiProvider>
 *   <App />
 * </ConfettiProvider>
 * ```
 *
 * To trigger the confetti animation from a child component, use the context:
 * ```tsx
 * const { show } = useContext(ConfettiContext);
 * show(); // Triggers confetti
 * ```
 */
export const ConfettiProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { width, height } = useWindowSize()
  const [isShowing, setIsShowing] = useState(false)
  const [numberOfPieces, setNumberOfPieces] = useState(200)

  const show = useCallback(
    (time = 3e3) => {
      if (isShowing) return
      setNumberOfPieces(500)
      setIsShowing(true)
      // 没有释放settimeout,此处需要优化
      const timeout = setTimeout(() => {
        const fadeInterval = setInterval(() => {
          setNumberOfPieces((prev) => {
            const newValue = prev - 10
            if (newValue <= 0) {
              clearInterval(fadeInterval)
              setIsShowing(false)
              return 0
            }
            return newValue
          })
        }, 100)
      }, time)
      return () => {
        clearTimeout(timeout)
      }
    },
    [isShowing],
  )

  return (
    <ConfettiContext.Provider value={{ show }}>
      {children}
      {isShowing && (
        <Confetti
          style={{ zIndex: 60 }}
          gravity={0.2}
          initialVelocityY={{ max: -1, min: -4 }}
          initialVelocityX={0}
          confettiSource={{
            x: 0,
            y: 0,
            w: width,
            h: 0,
          }}
          width={width}
          height={height}
          numberOfPieces={numberOfPieces}
          recycle={false}
        />
      )}
    </ConfettiContext.Provider>
  )
}

export const usePlayConfetti = () => {
  const context = useContext(ConfettiContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
